import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Description, Edit, GetApp, MoreVert } from '@material-ui/icons';
import { insertIf } from '@roc/feature-utils';
import { IconButton } from '@material-ui/core';
import {
  Button,
  FileUploadArea,
  StandardDialog,
  StatusDiv,
  UploadedFile,
} from '@roc/ui';
import { Document } from '@roc/feature-types';
import { useStore } from '@roc/client-portal-shared/hooks';
import { formatCurrency } from '@roc/ui/utils';
import { format as formatDate } from 'date-fns';
import { LeaseYesNoChip } from './leaseYesNoChip';
import { EditLeaseUnitModal } from './editLeaseUnitModal';
import { PreviewLeaseUnit } from './previewLeaseUnit';
import { LEASE_TYPE_LABELS } from '../store/leaseFormStore';

export interface LeaseUnitModalProps {
  open: boolean;
  onClose: () => void;
  document?: Document;
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: 0,
  },
  dropzoneLabel: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 700,
  },
  tableContainer: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  tableHeadCell: {
    fontWeight: 700,
    backgroundColor: '#FAFAFA',
    whiteSpace: 'nowrap',
  },
  emptyStateBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyIcon: {
    fontSize: '2.5rem',
    color: '#BDBDBD',
    marginBottom: theme.spacing(1),
  },
  emptyText: {
    color: '#757575',
  },
  closeButton: {
    border: '1px solid #D0D0D0',
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#FAFAFA',
    },
  },
  actionButton: {
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    padding: theme.spacing(0.75),
    marginLeft: theme.spacing(1),
    backgroundColor: '#fff',
  },
  tooltipContent: {
    fontSize: '0.875rem',
    padding: '4px 2px',
    display: 'inline-block',
  },
  documentLink: {
    color: '#4A90D9',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      color: '#2C6FAC',
    },
  },
}));

export const LeaseUnitModal = observer(
  ({ open, onClose, document }: LeaseUnitModalProps) => {
    const classes = useStyles();

    const [editLeaseOpen, setEditLeaseOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const { leaseUnitStore, globalStore, docReviewStoreV2 } = useStore();
    const uploadLocked = docReviewStoreV2.isUploadLocked(document);
    const { lease } = leaseUnitStore;

    const COLUMNS = [
      { label: 'Tenant Name' },
      { label: 'Unit' },
      { label: 'Vacant', align: 'center' as const },
      { label: 'Lease Type' },
      { label: 'Monthly Rent' },
      { label: 'Market Rent' },
      { label: 'Start Date', align: 'center' as const },
      { label: 'End Date', align: 'center' as const },
      { label: 'Document' },
      ...insertIf(globalStore.isInternalPortal, [{ label: 'Actions' }]),
    ];

    useEffect(() => {
      if (open && document?.loanId && document?.taskId) {
        leaseUnitStore.setContext(
          document.loanId,
          document.collateralId,
          document.taskId
        );
        leaseUnitStore.getLeaseUnitInfo();
      }
    }, [open, document?.loanId, document?.collateralId, document?.taskId]);

    const handleClose = () => {
      leaseUnitStore.setFiles([]);
      setPreviewOpen(false);
      onClose();
    };

    const handleLeaseUpload = (file: File) => {
      leaseUnitStore.setFiles([{ name: file.name, file }]);
      leaseUnitStore.uploadFile();
    };

    const dialogContent = (
      <>
        {(lease?.originalFileName || !uploadLocked) &&
          (lease?.originalFileName ? (
            <UploadedFile
              fileName={lease.originalFileName}
              onUpload={uploadLocked ? undefined : handleLeaseUpload}
              onView={() => setPreviewOpen(true)}
              disabled={leaseUnitStore.processingStatus === 'IN_PROGRESS'}
            />
          ) : (
            <>
              <Typography className={classes.dropzoneLabel}>
                Drop the lease document here, or click to browse
              </Typography>
              <FileUploadArea
                items={leaseUnitStore.files}
                setItems={updater => {
                  leaseUnitStore.setFiles(updater(leaseUnitStore.files));
                  leaseUnitStore.uploadFile();
                }}
              />
            </>
          ))}

        <Divider className={classes.divider} />

        {lease?.originalFileName && leaseUnitStore.processingStatus && (
          <Box mb={1.5}>
            <StatusDiv
              status={leaseUnitStore.processingStatus}
              text={
                leaseUnitStore.processingStatus === 'FAILED' &&
                leaseUnitStore.errorMessage
                  ? leaseUnitStore.errorMessage
                  : {
                      IN_PROGRESS:
                        'Processing - This may take a few seconds. Click Refresh to check for results.',
                      SUCCESS:
                        'Document ready – the uploaded file has been processed',
                      FAILED:
                        'Failed to read the document – needs manual processing',
                    }[leaseUnitStore.processingStatus] ?? ''
              }
              onRefresh={() => leaseUnitStore.getLeaseUnitInfo()}
            />
          </Box>
        )}

        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            Lease information
          </Typography>
        </Box>

        <TableContainer className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell
                    key={col.label}
                    className={classes.tableHeadCell}
                    align={col.align ?? 'left'}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lease && (
                <TableRow>
                  <TableCell>{lease.tenantName ?? '--'}</TableCell>
                  <TableCell>{lease.unitNumber ?? '--'}</TableCell>
                  <TableCell align="center">
                    <LeaseYesNoChip value={lease.vacant ? 'Yes' : 'No'} />
                  </TableCell>
                  <TableCell>
                    {lease.leaseType
                      ? LEASE_TYPE_LABELS[lease.leaseType] ?? lease.leaseType
                      : '--'}
                  </TableCell>
                  <TableCell>
                    {lease.monthlyRent != null
                      ? formatCurrency(lease.monthlyRent)
                      : '--'}
                  </TableCell>
                  <TableCell>
                    {lease.marketRent != null
                      ? formatCurrency(lease.marketRent)
                      : '--'}
                  </TableCell>
                  <TableCell align="center">{lease.startDate ?? '--'}</TableCell>
                  <TableCell align="center">{lease.endDate ?? '--'}</TableCell>
                  <TableCell>
                    {lease.originalFileName && (
                      <Tooltip
                        title={
                          document?.lastUploadDate ? (
                            <span className={classes.tooltipContent}>
                              {`Uploaded: ${formatDate(new Date(document.lastUploadDate), 'MMMM d, yyyy h:mm a')}`}
                            </span>
                          ) : (
                            ''
                          )
                        }
                        placement="top"
                      >
                        <span
                          className={classes.documentLink}
                          onClick={() => setPreviewOpen(true)}
                        >
                          {lease.originalFileName}
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                  {globalStore.isInternalPortal && (
                    <TableCell>
                      <IconButton
                        size="medium"
                        className={classes.actionButton}
                        disabled={
                          leaseUnitStore.processingStatus === 'IN_PROGRESS'
                        }
                        onClick={e => setMenuAnchor(e.currentTarget)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        getContentAnchorEl={null}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            leaseUnitStore.loadEditForm(lease);
                            setEditLeaseOpen(true);
                            setMenuAnchor(null);
                          }}
                        >
                          <ListItemIcon>
                            <Edit fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Edit" />
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!lease && (
            <Box className={classes.emptyStateBox} py={6}>
              <Description className={classes.emptyIcon} />
              <Typography className={classes.emptyText}>
                No records found yet. Upload the lease document above to get
                started.
              </Typography>
            </Box>
          )}
        </TableContainer>
      </>
    );

    const dialogActions = (
      <Button
        className={classes.closeButton}
        variant="outlined"
        onClick={handleClose}
        testId="lease-unit-modal-close"
      >
        Close
      </Button>
    );

    return (
      <>
        <StandardDialog
          open={open}
          title={document?.taskName ?? 'Lease Information Unit'}
          maxWidth="lg"
          fullWidth
          handleClose={handleClose}
          dialogContent={dialogContent}
          dialogActions={dialogActions}
          dialogContentclasses={{ root: classes.dialogContent }}
        />
        <EditLeaseUnitModal
          open={editLeaseOpen}
          onClose={() => setEditLeaseOpen(false)}
        />
        <StandardDialog
          open={previewOpen && lease != null}
          title={lease?.originalFileName ?? 'Document Preview'}
          handleClose={() => setPreviewOpen(false)}
          maxWidth="md"
          dialogContent={lease && <PreviewLeaseUnit lease={lease} />}
          dialogActions={
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetApp />}
              onClick={() => lease && leaseUnitStore.downloadLease(lease.leaseId)}
              testId="lease-unit-modal-download"
            >
              Download
            </Button>
          }
        />
      </>
    );
  }
);
